import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux'

import SaveButton from '../components/psc/lib/SaveButton'
import ErrorMessage from '../components/psc/lib/ErrorMessage'
import CancelButton from '../components/psc/lib/CancelButton'
import DeleteButton from '../components/psc/lib/DeleteButton'
import { PscPhrase } from '../components/psc/psc-phrase';
import SingleSelect from '../components/psc/lib/SingleSelect'
import PickDate from '../components/psc/lib/PickDate'
import Loading from '../components/psc/lib/Loading'
import NoAccess from '../components/psc/lib/NoAccess'

import LinkedItems from '../components/psc/lib/LinkedItems'
import TextArea from '../components/psc/lib/TextArea'

import useAuthentication from '../hooks/useAuthentication';
import useUserManagement from '../hooks/useUserManagement';
import useBrowserTabTitle from '../hooks/useBrowserTabTitle';

const SerialNumber = () => {
    const pscState = useSelector(state => state.pscState.value)
    const config = pscState.config || {}
    const params = useParams();
    const isNew = params.objID === 'new';
    const objID = Number(params.objID);

    const { setBrowserTabTitle } = useBrowserTabTitle()
    
    const { currentUser } = useAuthentication();

    const {
        userHasRole,
        userHasProjectAccessLevel,
    } = useUserManagement();

    const [error, setError] = useState([])
    const [submitActive, setSubmitActive] = useState(false)
    const [updatedAt, setUpdatedAt] = useState(null)
    const [submitSpinner, setSubmitSpinner] = useState(false)
    const [linkedItems, setLinkedItems] = useState([])
    const [organisationCode, setOrganisationCode] = useState(config.serial_numbers_default_organisation_code || 'GNC')
    const [reference, setReference] = useState('')
    const [title, setTitle] = useState('')
    const [description, setDescription] = useState('')
    const [createDate, setCreateDate] = useState(new Date())
    const [projectID, setProjectID] = useState(0)
    const [projects, setProjects] = useState([])
    const [serialNumberNumber, setSerialNumberNumber] = useState(0)
    const [ciNumber, setCiNumber] = useState('')
    
    const [hasLoaded, setHasLoaded] = useState(isNew ? true: false)
    const [hasAccess, setHasAccess] = useState(true)
    
    useEffect(() => {
        const projectsUrl = config.serial_numbers_project_fetch_url || '/v0/projects?is_archived=0'

        fetch(projectsUrl, { credentials: 'same-origin' })
            .then(function(response) {
                return response.json()
            })
            .then(function(json) {
                setProjects(json.results)
            })
    }, [config.serial_numbers_project_fetch_url])

    useEffect(() => {
        if( ! isNew ) {
            return
        }
        
        fetch('/v0/serial_numbers/reference', {
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json'
            },
            method: 'POST',
            body: JSON.stringify({
                project_id: projectID,
                organisation_code: organisationCode,
            })
        }).then(function(response) {
            return response.json()
        }).then(function(json) {
            setReference(json.result.reference)
        })
    }, [isNew, projectID, organisationCode])

    useEffect(() => {
        if( isNew ) {
            setBrowserTabTitle('New Serial Number')
            return
        }
        if( reference || title ) {
            setBrowserTabTitle(`${reference} ${title}`.trim())
        }
    }, [isNew, reference, title, setBrowserTabTitle])

    useEffect(() => {
        if ( isNew ) {
            return
        }

        fetch('/v0/serial_numbers/'+objID, { credentials: 'same-origin' })
            .then(function(response) {
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }
                return response.json();
            })
            .then(function(json) {
                if (json.error || json.result.id === 0) {
                    throw new Error(json.error);
                }
                setReference(json.result.reference)
                setOrganisationCode(json.result.organisation_code)
                setTitle(json.result.title)
                setDescription(json.result.description)
                setCreateDate(json.result.create_date)
                setProjectID(json.result.project_id)
                setSerialNumberNumber(json.result.serial_number_number)
                setLinkedItems(json.result.linked_items)
                setCiNumber(json.result.ci_number)
                setUpdatedAt(json.result.updated_at)
                setHasLoaded(true)
            })
            .catch(function(error) {
                console.log('Error fetching serial number:', error);
                setHasAccess(false);
            })
    }, [isNew, objID])

    const toBackend = () => {
        return {
            updated_at: updatedAt,
            reference: reference,
            organisation_code: organisationCode,
            title: title,
            description: description,
            create_date: createDate,
            project_id: projectID,
            serial_number_number: serialNumberNumber,
            linked_items: linkedItems,
            ci_number: ciNumber
        }
    }

    const handleSubmitError = (props) => {
        setSubmitActive(false)
        setSubmitSpinner(false)
        setError([ props.message ])
    }

    const handleSubmit = (event) => {
        if( event ) {
            event.preventDefault()
        }

        if( submitActive ) {
            return null
        }

        let error = []

        if ( ! projectID ) {
            error.push('Origin is required')
        }

        if ( ! organisationCode ) {
            error.push('Organisation Code is required')
        }

        if ( ! title || /^\s+$/.test(title) ) {
            error.push('Title is required')
        }

        if ( ! createDate ) {
            error.push('Added date is required')
        }

        if ( error.length > 0 ) {
            setError(error)
            return null
        }

        setSubmitActive(true)
        setSubmitSpinner(true)

        let submit = {
            objType: 'serial_numbers',
            objID: objID,
            data: toBackend(),
        }
        return submit
    }

    const globalDisabled = () => {
        if( ! userHasRole(currentUser, 'serial_numbers_user') ) {
            return true
        }
    }

    let requiredIndicator = '';
    if(userHasRole(currentUser, 'serial_numbers_user')) {
        requiredIndicator = (<label className="form-required-star">&nbsp;*</label>)
    }

    if( ! hasAccess ) {
        return (<NoAccess />)
    }
    let deleteButton = []
    if ( !isNew && userHasRole(currentUser, 'serial_numbers_admin') ) {
        const url = '/v0/serial_numbers/'+objID;
        deleteButton.push(
            <DeleteButton
                key="delete-button"
                url={url}
                onError={handleSubmitError}
            />
        )
    }

    const projectsList = projects.map(item => {
        item.label = item.project_code + ' - ' + item.name
        if( ! userHasProjectAccessLevel(currentUser, item.id, "READONLY") ) {
            item.isDisabled = true
        }
        return item
    })
    
    if (!hasLoaded) {
        return <Loading />
    }
    
    return (
        <div className="wrapper wrapper-content">
            <div className="row">
                <div className="col-lg-12">
                    <div className="ibox float-e-margins">
                        <div className="ibox-title">
                            <h2>Serial Number: {reference || '<New Serial Number>'} {title}</h2>
                        </div>
                        <div className="ibox-content">
                            <form className="form-horizontal" onSubmit={(e) => handleSubmit(e)}>

                                <div className="form-group">
                                    <label className="col-sm-2 control-label">Origin
                                        {requiredIndicator}
                                    </label>
                                    <div className="col-sm-8">
                                        <SingleSelect
                                            value={projectID}
                                            onChange={(project) => { setProjectID(project.id); setLinkedItems([]) }}
                                            options={projectsList}
                                            disabled={!isNew || globalDisabled()}
                                        />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="col-sm-2 control-label">{PscPhrase('SERIAL_NUMBER_LABEL_ORG_CODE')}
                                        {requiredIndicator}
                                    </label>
                                    <div className="col-sm-8">
                                        <input className="form-control" type="text" name="organisation_code"
                                            value={organisationCode}
                                            onChange={(e) => setOrganisationCode(e.target.value.toUpperCase())}
                                            maxLength="4"
                                            disabled={!isNew || globalDisabled()}
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="col-sm-2 control-label">Serial Number</label>
                                    <div className="col-sm-8">
                                        <input className="form-control" type="text" name="reference"
                                            value={reference}
                                            disabled="true"
                                        />
                                        <span className="help-block m-b-none m-t-none">
                                            <small className="form-info-text" > {PscPhrase('SERIAL_NUMBER_CREATED_AUTOMATICALLY')}</small>
                                        </span>
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="col-sm-2 control-label">Title
                                        {requiredIndicator}
                                    </label>
                                    <div className="col-sm-8">
                                        <input className="form-control" type="text" name="title" value={title} onChange={(e) => setTitle(e.target.value)} disabled={globalDisabled()} required />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="col-sm-2 control-label">{PscPhrase('SERIAL_NUMBER_CI_NUMBER')}</label>
                                    <div className="col-sm-8">
                                        <input className="form-control" type="text" value={ciNumber} onChange={(e) => setCiNumber(e.target.value)} disabled={globalDisabled()} />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="col-sm-2 control-label">{PscPhrase('SERIAL_NUMBER_LABEL_DESCRIPTION')}</label>
                                    <div className="col-sm-8">
                                        <TextArea className="form-control" name="description" value={description} onChange={(e) => setDescription(e.target.value)} disabled={globalDisabled()} />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="col-sm-2 control-label">{PscPhrase('SERIAL_NUMBER_LABEL_CREATE_DATE')}
                                        {requiredIndicator}
                                    </label>
                                    <div className="col-sm-8">
                                        <PickDate
                                            value={createDate}
                                            onChange={(date) => setCreateDate(date)}
                                            disabled={globalDisabled()}
                                        />
                                    </div>
                                </div>

                                <div className="hr-line-dashed"></div>

                                <LinkedItems
                                    header={PscPhrase('SERIAL_NUMBER_LABEL_REFERENCE_DOCS')}
                                    project_id={projectID}
                                    linkedItems={linkedItems}
                                    disabled={globalDisabled()}
                                    onChange={(value) => setLinkedItems(value)}
                                />

                                <div className="hr-line-dashed"></div>

                                <div className="form-group">
                                    <ErrorMessage
                                        error={error}
                                    />

                                    <div className="col-sm-8 col-sm-offset-2">
                                        <CancelButton/>

                                        <SaveButton
                                            buttonVariant='primary'
                                            visible={userHasRole(currentUser, 'serial_numbers_user')}
                                            spinnerActive={submitSpinner}
                                            label='Save Changes'
                                            onClick={handleSubmit}
                                            onError={handleSubmitError}
                                        />

                                        {deleteButton}
                                    </div>
                                </div>

                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
};

export default SerialNumber
